// Where the signed-in person has a Discover package (keepr-api KPR-364, epic
// KPE-035; docs/SCHEMA.md "Installed on Discover"). GET /api/packages and
// GET /api/packages/{key} carry `installedIn` for them:
//
//   [{ collectionId, name, icon, color, version, addOns, canManage,
//      state, cards: { live, total }, updateAvailable? }]
//
// `state` is `installed` (every card the install made is live) or `partial`
// (some were deleted or kept as the person's own; keepr-api KPR-1135). A
// collection with no live card is not listed at all, so this file never says
// "not installed" about an entry: it only words what is there.
//
// The server decides everything that is a rule: which collections the person
// reads, who manages, and whether a manager's collection is behind
// (`updateAvailable`, absent for a reader). This file only turns that into
// words and into the few questions the screens ask, so no screen re-derives
// "behind" from version numbers. Rejected: comparing `version` with the
// package's here — it would ignore a retired package, an ignored version and
// a collection package, which the server knows and the client does not.

const list = pkg => (Array.isArray(pkg?.installedIn) ? pkg.installedIn : []);

const countOf = v => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : null);

/** Is this entry a partial install (some of its cards are gone)? Only what the server says. */
export function isPartial(entry) {
	return entry?.state === 'partial' && countOf(entry?.cards?.live) !== null && countOf(entry?.cards?.total) !== null;
}

/** "6 of 7 cards", or '' when the entry is whole or says no counts. */
export function cardsLeftWords(entry) {
	return isPartial(entry) ? `${countOf(entry.cards.live)} of ${countOf(entry.cards.total)} cards` : '';
}

/**
 * What a collection has of the bundle, in the product's words: *Installed*
 * when every card is live, *Installed (6 of 7 cards)* when some were deleted
 * or kept. One set of words for the tile, the pack page's rows and the Card
 * Library group, so they cannot say different things.
 */
export function installedWords(entry) {
	const left = cardsLeftWords(entry);
	return left ? `Installed (${left})` : 'Installed';
}

/** The entry that is most whole: a complete one, else the partial with the largest share of its cards left. */
function fullest(entries) {
	const whole = entries.find(entry => !isPartial(entry));
	if (whole) return whole;
	const share = entry => countOf(entry.cards.live) / (countOf(entry.cards.total) || 1);
	return entries.reduce((best, entry) => (share(entry) > share(best) ? entry : best), entries[0]);
}

/**
 * `{ installed, behind, count, partial, words }`: held anywhere the person
 * reads; a collection they manage is behind. `words` is the badge when nothing
 * is behind: *Installed* if any collection holds the whole bundle, else the
 * words of the collection that holds most of it (`partial` says it is not the
 * whole bundle). The rows under it say what each collection has.
 */
export function installedState(pkg) {
	const entries = list(pkg);
	const best = entries.length ? fullest(entries) : null;
	return {
		installed: entries.length > 0,
		behind: entries.some(entry => entry.canManage === true && entry.updateAvailable === true),
		count: entries.length,
		partial: !!best && isPartial(best),
		words: best ? installedWords(best) : '',
	};
}

/** "Installed in Home", "Installed in Home and 2 more"; '' when it is held nowhere. */
export function installedInWords(pkg) {
	const entries = list(pkg);
	if (!entries.length) return '';
	const first = `Installed in ${entries[0].name || 'a collection'}`;
	return entries.length === 1 ? first : `${first} and ${entries.length - 1} more`;
}

/** "v5", or "v5 · v6 available" for a manager whose collection is behind; '' when no version is recorded. */
export function versionWords(entry, pkg) {
	const have = Number(entry?.version);
	if (entry?.version == null || !Number.isFinite(have)) return '';
	const behind = entry.canManage === true && entry.updateAvailable === true;
	return behind ? `v${have} · v${Number(pkg?.version) || have + 1} available` : `v${have}`;
}

/** The add-ons of the bundle a collection does not hold yet (a collection package has none). */
export function missingAddOns(pkg, entry) {
	if (!pkg || pkg.kind === 'collection') return [];
	const held = new Set(Array.isArray(entry?.addOns) ? entry.addOns : []);
	return (Array.isArray(pkg.addOns) ? pkg.addOns : []).filter(addOn => addOn && !held.has(addOn.key));
}

/** The names of the add-ons a collection holds, in the package's order; a key the package no longer lists keeps its key. */
export function heldAddOnNames(pkg, entry) {
	const held = Array.isArray(entry?.addOns) ? entry.addOns : [];
	if (!held.length) return [];
	const byKey = new Map((Array.isArray(pkg?.addOns) ? pkg.addOns : []).map(addOn => [addOn.key, addOn.name]));
	const known = (Array.isArray(pkg?.addOns) ? pkg.addOns : []).filter(addOn => held.includes(addOn.key)).map(addOn => addOn.name);
	return [...known, ...held.filter(key => !byKey.has(key))];
}

/** The line under a row's collection: how much of it is left, its version, then the add-ons it holds. */
export function rowWords(entry, pkg) {
	const names = heldAddOnNames(pkg, entry);
	// How much of it is left first (keepr-api KPR-1135), when the server says.
	const state = entry?.state === 'installed' || entry?.state === 'partial' ? installedWords(entry) : null;
	return [state, versionWords(entry, pkg), names.length ? `Add-ons: ${names.join(', ')}` : null].filter(Boolean).join(' · ');
}
